import type { MouseEvent } from 'react';
import type { ContentTypeDefinition } from '../../lib/flotiq-client/types/api-response';

import { use, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { twMerge } from 'tailwind-merge';

import { EMPTY_FUNCTION } from '../../lib/globals';
import { getTestProps } from '../../lib/helpers';
import { RolePermissions } from '../../lib/rolePermissions';

import useSelectedSpace from '../../hooks/useSelectedSpace';

import UserContext from '../../contexts/UserContext';

import {
  ArrowRightCurvedIcon,
  ClipboardIcon,
  DuplicateSolidIcon,
  EllipsisVerticalIcon,
  PencilSolidIcon,
  PencilSquareIcon,
  ReloadIcon,
  TrashSolidIcon,
} from '../../images/shapes';

import Card from '../Card/Card';
import DateContainer from '../DateContainer/DateContainer';
import Heading from '../Heading/Heading';
import { PopoverPanelPlacement } from '../Popover/classes';
import Popover from '../Popover/Popover';
import PopoverItem from '../Popover/PopoverItem/PopoverItem';
import StatusPill from '../StatusPill/StatusPill';

interface TypeDefinitionCardProps {
  /**
   * Image url
   */
  imageUrl?: string;
  /**
   * Image alt attribute
   */
  imageUrlAlt?: string;
  /**
   * If image url is loading
   */
  imageUrlLoading?: boolean;
  /**
   * Content type data
   */
  contentType:
    | {
        id: string;
        name: string;
        label: string;
        folder: boolean;
        children: ContentTypeDefinition[];
      }
    | ContentTypeDefinition;
  /**
   * Content objects count
   */
  objectCount?: number;
  /**
   * CT delete callback
   */
  onDelete?: (name?: string) => void;
  /**
   * If card represents folder
   */
  isFolder?: boolean;
  /**
   * If limit of ctds is reached
   */
  limitReached?: boolean;
  /**
   * Callback when folder is clicked
   */
  onClickFolder?: (name: string) => void;
  /**
   * Test id of the card
   */
  testId?: string;
}

const TypeDefinitionCard = ({
  imageUrl = '',
  imageUrlAlt = '',
  imageUrlLoading = false,
  contentType,
  objectCount = 0,
  onDelete = EMPTY_FUNCTION,
  testId = '',
  isFolder = false,
  onClickFolder,
  limitReached = false,
}: TypeDefinitionCardProps) => {
  const { t } = useTranslation();
  const { permissions } = use(UserContext);
  const { buildUrlWithSpace } = useSelectedSpace();
  const { canDelete, canRead } = useMemo(
    () =>
      isFolder
        ? { canDelete: true, canRead: true }
        : permissions.getCtdPermissions(contentType.name) || {},
    [contentType.name, isFolder, permissions],
  );
  const canReadCo = isFolder ? true : permissions.canCo(contentType.name);
  const canCreateNewCtd = permissions.canCtd(
    '*',
    RolePermissions.PERMISSIONS_TYPES.CREATE,
  );

  const popoverButton = (
    <div
      className="flex items-center justify-center w-8 h-8 rounded-full bg-slate-400/50 hover:bg-slate-400/80"
      {...getTestProps(testId, 'menu-toggle-button')}
    >
      <EllipsisVerticalIcon className="h-5 text-white" />
    </div>
  );

  const popoverMenuContentItems = useMemo(
    () => [
      {
        label: t('TypeDefinitionCard.Edit'),
        key: 'edit',
        icon: <PencilSolidIcon className="w-3 min-w-3" />,
        link: buildUrlWithSpace(
          `content-type-definitions/edit/${contentType.name}`,
        ),
      },
      {
        label: t('TypeDefinitionCard.GoTo'),
        key: 'go',
        icon: <ArrowRightCurvedIcon className="w-3.5 min-w-3.5" />,
        link: buildUrlWithSpace(`content-type-objects/${contentType.name}`),
        disabled: !canReadCo,
      },
      {
        label: t('TypeDefinitionCard.DuplicateDefinition'),
        key: 'duplicate',
        icon: <DuplicateSolidIcon className="w-3.5 min-w-3.5" />,
        link: buildUrlWithSpace(
          `content-type-definitions/duplicate/${contentType.name}`,
        ),
        disabled: !canCreateNewCtd || limitReached,
      },
      {
        label: (
          <span className="text-red">{t('TypeDefinitionCard.Delete')}</span>
        ),
        key: 'delete',
        icon: <TrashSolidIcon className="w-3 min-w-3 text-red" />,
        onClick: (e: MouseEvent<HTMLButtonElement>) => {
          e.stopPropagation();
          e.preventDefault();
          onDelete(contentType.name);
        },
        disabled: !canDelete,
      },
    ],
    [
      t,
      buildUrlWithSpace,
      contentType.name,
      canReadCo,
      canCreateNewCtd,
      limitReached,
      canDelete,
      onDelete,
    ],
  );

  const popoverMenu = (
    <>
      {popoverMenuContentItems?.map(({ key, ...item }, idx, items) => {
        const isLast = idx === items.length - 1;
        return (
          <PopoverItem
            key={key}
            color={isLast ? 'red' : 'black'}
            {...item}
            {...getTestProps(testId, `item-${key}`, 'testId')}
          />
        );
      })}
    </>
  );

  return (
    <Card
      link={
        canRead && !isFolder
          ? buildUrlWithSpace(
              `content-type-definitions/edit/${contentType.name}`,
            )
          : undefined
      }
      onClick={isFolder ? () => onClickFolder?.(contentType.name) : undefined}
      imageUrl={imageUrl}
      imageUrlAlt={imageUrlAlt}
      imageUrlLoading={imageUrlLoading}
      imageOverlay={
        <div className="flex flex-col h-full p-3 justify-between">
          <div className="flex justify-between transition duration-200 ease-in-out p-1 space-x-2 z-0">
            {canReadCo && (
              <StatusPill
                link={
                  !isFolder
                    ? buildUrlWithSpace(
                        `content-type-objects/${contentType.name}`,
                      )
                    : undefined
                }
                status={
                  <div className="flex items-center">
                    <ClipboardIcon
                      className={twMerge(
                        'w-3 mr-1.5 text-slate-900',
                        isFolder
                          ? 'dark:text-orange-300'
                          : 'dark:text-lime-200',
                      )}
                    />
                    {isFolder
                      ? t('TypeDefinitionCard.TypeNumber', {
                          count: objectCount,
                        })
                      : t('TypeDefinitionCard.ObjectNumber', {
                          count: objectCount,
                        })}
                  </div>
                }
                containerClasses={twMerge(
                  'pointer-events-auto font-medium! py-1 flex items-center border-1',
                  isFolder
                    ? [
                        'bg-orange text-slate-700 border-orange',
                        'hover:bg-orange-600 hover:border-orange-600',
                        'dark:bg-orange-950 dark:text-orange-300 dark:border-orange-900',
                        'dark:hover:bg-orange-900 dark:hover:border-orange-900 dark:hover:text-orange-300',
                      ]
                    : [
                        'bg-lime text-slate-700 border-lime',
                        'hover:bg-lime-500 hover:border-lime-500',
                        'dark:bg-lime-950 dark:text-lime-200 dark:border-lime-800',
                        'dark:hover:bg-lime-800 dark:hover:border-lime-800 dark:hover:text-lime-200',
                      ],
                )}
                onClick={
                  isFolder ? () => onClickFolder?.(contentType.name) : undefined
                }
                testId={testId ? `${testId}-pill` : ''}
              />
            )}
            {canRead && !isFolder && (
              <div className="ml-auto pointer-events-auto">
                <Popover
                  content={popoverMenu}
                  popoverButton={popoverButton}
                  popoverPanelPlacement={PopoverPanelPlacement.LeftBottom}
                  popoverPhonePanelPlacement={PopoverPanelPlacement.LeftBottom}
                  additionalPopoverPanelClasses="w-max mr-2!"
                />
              </div>
            )}
          </div>
        </div>
      }
      body={
        <div className="bg-white dark:bg-slate-800 p-5 space-y-2">
          <Heading
            level={2}
            additionalClasses="text-xl pt-1 pb-2 leading-none dark:text-white"
          >
            {contentType.label}
          </Heading>
          <DateContainer
            date={
              'createdAt' in contentType ? contentType.createdAt : undefined
            }
            icon={<PencilSquareIcon className="text-blue w-4 mr-2" />}
            text={t('TypeDefinitionCard.Created')}
          />
          <DateContainer
            date={
              'updatedAt' in contentType ? contentType.updatedAt : undefined
            }
            icon={<ReloadIcon className="text-blue w-4 mr-2" />}
            text={t('TypeDefinitionCard.Updated')}
          />
        </div>
      }
      additionalContainerClasses={'w-full'}
      additionalImageContainerClasses="aspect-[1.5] overflow-visible"
      darkenOnHover={canReadCo}
      testId={testId}
    />
  );
};

export default TypeDefinitionCard;
